import styles from "./DisruptorBlock.module.scss";
import classNames from "classnames/bind";
import { ESIInclude } from "@packages/esi-include/SimpleESIInclude";
import {
  FeatureFlag,
  FeatureFlags,
  FeatureFlagValue,
} from "@packages/feature-flags";

const cx = classNames.bind(styles);

export type DisruptorBlockDetails = {
  id?: string;
  gridIndex: number;
  maxWidth: number;
  esiPath?: string;
};

export const DisruptorBlock = ({
  indexInGrid,
  productCount,
  flags,
  finalCreditBannerIndex,
  className,
}: {
  indexInGrid: number;
  productCount: number;
  flags: FeatureFlags;
  finalCreditBannerIndex: number;
  className?: string;
}) => {
  const disruptorBlockDetails = getDisruptorBlockDetails(
    flags,
    finalCreditBannerIndex,
  );

  if (
    !shouldDisplayDisruptorBlock(
      indexInGrid,
      disruptorBlockDetails,
      productCount,
    )
  ) {
    return null;
  }

  return (
    <div
      data-component="DisruptorBlock"
      data-disruptor-block={disruptorBlockDetails.id}
      data-testid="disruptor-block"
      className={cx(
        "disruptor-block",
        `disruptor-block--${disruptorBlockDetails.maxWidth}-span`,
        className,
      )}
    >
      {disruptorBlockDetails.esiPath && (
        <ESIInclude
          src={disruptorBlockDetails.esiPath}
          id="web-plp-scaffold-ui-disruptor-block"
        />
      )}
    </div>
  );
};

const getDisruptorBlockDetails = (
  flags: FeatureFlags,
  finalCreditBannerIndex: number,
): DisruptorBlockDetails => {
  const experienceId =
    flags?.disruptorBlockId?.source === "monetate"
      ? flags?.disruptorBlockId?.experienceId
      : undefined;

  return {
    id: extractStringValue(
      getFlagByExperienceId(flags?.disruptorBlockId, experienceId),
    ),
    gridIndex:
      (extractNumberValue(
        getFlagByExperienceId(flags?.disruptorBlockGridIndex, experienceId),
      ) || 11) + finalCreditBannerIndex,
    maxWidth:
      extractNumberValue(
        getFlagByExperienceId(flags?.disruptorBlockMaxWidth, experienceId),
      ) || 3,
    esiPath: extractStringValue(
      getFlagByExperienceId(flags?.disruptorBlockEsiPath, experienceId),
    ),
  };
};

const getFlagByExperienceId = (
  flag: FeatureFlag | undefined,
  experienceId?: number,
): FeatureFlagValue | undefined => {
  if (!flag) {
    return undefined;
  }

  if (flag.source === "monetate" && experienceId) {
    return experienceId === flag.experienceId ? flag.value : undefined;
  }

  if (flag.source !== "monetate" && !experienceId) {
    return flag.value;
  }

  return undefined;
};

const shouldDisplayDisruptorBlock = (
  ind: number,
  disruptorBlockDetails: DisruptorBlockDetails,
  productCount: number,
) => {
  if (!disruptorBlockDetails.id) {
    return false;
  }

  // this is the disruptor block index
  if (disruptorBlockDetails?.gridIndex === ind) {
    return true;
  }

  // this is last product & disruptor block index is after last product
  if (
    ind === productCount - 1 &&
    disruptorBlockDetails?.gridIndex >= productCount
  ) {
    return true;
  }

  return false;
};

const extractStringValue = (value: string | boolean | null | undefined) =>
  typeof value === "string" ? value : undefined;

const extractNumberValue = (value: string | boolean | null | undefined) =>
  typeof value === "string" ? parseInt(value, 10) : undefined;
